import type { TRadioChartLeader, TRadioPlatform, TRadioTasteBuddy, TRadioTrack, TRadioTrackInteraction } from "@/types"
import { api } from "./api"
import type { IAPIParams } from "./types"
import { generateAPIResource } from "./utils"

interface IDetectedPlatform {
  platform: TRadioPlatform
  displayName: string
}

const platformPatterns: { pattern: RegExp; platform: TRadioPlatform; displayName: string }[] = [
  { pattern: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)/i, platform: "youtube", displayName: "YouTube" },
  { pattern: /^(https?:\/\/)?(www\.)?soundcloud\.com/i, platform: "soundcloud", displayName: "SoundCloud" },
  { pattern: /^(https?:\/\/)?(www\.)?vimeo\.com/i, platform: "vimeo", displayName: "Vimeo" },
  { pattern: /^(https?:\/\/)?(www\.)?twitch\.tv/i, platform: "twitch", displayName: "Twitch" },
  { pattern: /^(https?:\/\/)?(www\.)?vk\.com/i, platform: "vk", displayName: "VK" },
  {
    pattern: /^(https?:\/\/)?(www\.)?music\.yandex\.(ru|com|by|kz)/i,
    platform: "yandex_music",
    displayName: "Yandex Music",
  },
]

export function detectPlatform(url: string): IDetectedPlatform {
  const match = platformPatterns.find(({ pattern }) => pattern.test(url))
  if (match) {
    return { platform: match.platform, displayName: match.displayName }
  }
  return { platform: "other", displayName: "Link" }
}

export const radioAPI = {
  ...generateAPIResource<TRadioTrack>("/radio/tracks", {
    create: false,
    update: false,
    remove: false,
  }),

  addTrack: (payload: { url: string; title?: string; artist?: string; thumbnail?: string; duration?: number }) =>
    api.post<TRadioTrack>("/radio/tracks", payload).then((res) => res.data),

  remove: (id: string) => api.delete<string>(`/radio/tracks/${id}`).then((res) => res.data),

  toggleLike: (id: string) => api.post<TRadioTrackInteraction>(`/radio/tracks/${id}/like`).then((res) => res.data),

  toggleHide: (id: string) => api.post<TRadioTrackInteraction>(`/radio/tracks/${id}/hide`).then((res) => res.data),

  chartLeaders: (params?: IAPIParams) =>
    api.get<TRadioChartLeader[]>("/radio/chart-leaders", { params }).then((res) => res.data),

  tasteBuddies: () => api.get<TRadioTasteBuddy[]>("/radio/chart-leaders/taste-buddies").then((res) => res.data),

  notifyPlay: (id: string) => api.post(`/radio/tracks/${id}/play`),

  fetchMeta: (url: string) =>
    api
      .get<{
        url: string
        platform: TRadioPlatform
        title: string | null
        artist: string | null
        thumbnail: string | null
      }>("/radio/meta", { params: { url } })
      .then((res) => res.data),
}
